/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from "@angular/router";

import { Antenna } from "../../models/antenna";
import { AntennaInstance } from "../../models/antenna-instance";
import { EmGroup } from "../../models/em-group";
import { GroupType } from "../../models/group-type";
import { InstanceData } from "../../models/instance-data";
import { ReaderInstance } from "../../models/reader-instance";

import { AntennaService } from "../../services/antenna.service";
import { GroupService } from "../../services/group.service";
import { ReaderService } from "../../services/reader.service";

import { AddToGroupDialogComponent } from "../add-to-group-dialog.component";
import { ConfirmDeleteDialogComponent } from "../confirm-delete-dialog.component";
import { ManageGroupsDialogComponent } from "../manage-groups-dialog.component";
import { RemoveFromGroupDialogComponent } from "../remove-from-group-dialog.component";
import { ShowGroupDialogComponent } from "../show-group-dialog.component";

import { EntityInstanceDialogComponent } from './dialogs/entity-instance-dialog.component';
import { EntityTypeDialogComponent } from "./dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE } from '../../app-constants';
import { Observable } from "rxjs";
import { BaseInstance } from "../../models/base-instance";
import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

const FilterOption_RemoveFilter = { column: null, label: "REMOVE FILTER" } as FilterOption;

@Component({
  templateUrl: './antennas.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class AntennasComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mAntennas: AntennaUI[] = [];
  mAntennaInstances: AntennaInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Antennas, 2 = Antenna Instances


  //Entity Type Dialog Variables
  mEntity: Antenna;
  mEntityName: string;
  mEntityType = GroupType.ANTENNA;
  mEntityTypeName = "Antenna";

  typeaheadLoading: boolean;
  entities: Observable<BaseInstance[]>;

  //Entity Instance Dialog Variables
  mInstance: AntennaInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: Antenna;
  mInstanceType = GroupType.ANTENNAINSTANCE;
  mInstanceTypeName = "Antenna";
  mAvailableEntityTypes: Antenna[];
  mSelectedReader: ReaderInstance;
  mAvailableReaders: ReaderInstance[];

  // @author- rushi 
  currentPageForAntenna = 1;
  currentPageForAntennaInstance = 1;

  totalNumberOfAntenna = 0;
  totalNumberOfAntennaInstance = 0;

  // All the filter Options available
  filterOptionForAntenna: FilterOption[] = [];
  filterOptionForAntennaInstance: FilterOption[] = [];

  // The Filter Option selected by the user
  selectedFilterOptionForAntenna: FilterOption = FilterOption_RemoveFilter;
  selectedFilterOptionForAntennaInstance: FilterOption = FilterOption_RemoveFilter;

  // Filter Value for the Filter Option selected by user
  filterOptionValueForAntenna: string = "";
  filterOptionValueForAntennaInstance: string = "";

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];


  constructor(
    private mRoute: ActivatedRoute,
    private mAntennaService: AntennaService,
    private mGroupService: GroupService,
    private mReaderService: ReaderService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) { }

  ngOnInit(): void {

    // Get all the available Filter Options
    this.refreshFilterOptionsForAntenna();
    this.refreshFilterOptionsForAntennaInstance();

    // default the results per page to 0th element of array
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data: {
        antennas: PagedResults<Antenna>,
        antennaInstances: PagedResults<AntennaInstance>
      }
      ) => {
        this.updatePagedAntenna(data.antennas);
        this.updatePagedAntennaInstance(data.antennaInstances);
      });
  }

  // Handle the result per page clicked button
  manageResultPerPageClickedForTypes(value: number ): void {
    this.selectedPageSizeValueForTypes = value;
    // selecting any value will bring you back to first page
    this.currentPageForAntenna = 1;
    this.refreshAntennas();
  }


  manageResultPerPageClickedForInstances(value: number): void {
    this.selectedPageSizeValueForInstances = value;
    // selecting any value will bring you back to first page
    this.currentPageForAntennaInstance = 1;
    this.refreshAntennaInstance();
  }

  isSingleAntennaSelected(): boolean {
    if (this.mAntennas != null && this.mAntennas.length > 0) {
      let oneSelected = false;
      for (const antenna of this.mAntennas) {
        if (antenna.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyAntennasSelected(): boolean {
    return this.mAntennas.some(antenna => antenna.mSelected);
  }

  isAllAntennasSelected(): boolean {
    return this.mAntennas !== null && this.mAntennas.every(antenna => antenna.mSelected);
  }

  setAllAntennasSelected(event: any): void {
    this.mAntennas.forEach(antenna => antenna.mSelected = event.target.checked);
  }

  isSingleAntennaInstanceSelected(): boolean {
    if (this.mAntennaInstances != null && this.mAntennaInstances.length > 0) {
      let oneSelected = false;
      for (const antennaInstance of this.mAntennaInstances) {
        if (antennaInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyAntennaInstancesSelected(): boolean {
    return this.mAntennaInstances.some(antennaInstance => antennaInstance.mSelected);
  }

  isAllAntennaInstancesSelected(): boolean {
    return this.mAntennaInstances !== null && this.mAntennaInstances.every(antennaInstance => antennaInstance.mSelected);
  }

  setAllAntennaInstancesSelected(event: any): void {
    this.mAntennaInstances.forEach(antennaInstance => antennaInstance.mSelected = event.target.checked);
  }

  getSelectedAntennas(): Antenna[] {
    const antennas: Antenna[] = [];
    this.mAntennas.filter(ui => ui.mSelected).forEach(ui => antennas.push(ui.mAntenna));
    return antennas;
  }

  getSelectedAntennaIds(): number[] {
    const ids: number[] = [];
    this.mAntennas.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mAntenna.id));
    return ids;
  }

  getSelectedAntennaNames(): string[] {
    const names: string[] = [];
    this.mAntennas.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mAntenna.name));
    return names;
  }





  getSelectedAntennaInstances(): AntennaInstance[] {
    const antennaInstances: AntennaInstance[] = [];
    this.mAntennaInstances.filter(ui => ui.mSelected).forEach(ui => antennaInstances.push(ui.mAntennaInstance));
    return antennaInstances;
  }

  getSelectedAntennaInstanceIds(): number[] {
    const ids: number[] = [];
    this.mAntennaInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mAntennaInstance.id));
    return ids;
  }

  getSelectedAntennaInstanceNames(): string[] {
    const names: string[] = [];
    this.mAntennaInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mAntennaInstance.name));
    console.log(names);
    return names;
  }

  manageAntennaGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.ANTENNA);
  }

  manageAntennaInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.ANTENNAINSTANCE);
  }


  addSelectedAntennasToGroupClicked(): void {
    if (this.isAnyAntennasSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.ANTENNA, this.getSelectedAntennaNames());
    }
  }

  addSelectedAntennaInstancesToGroupClicked(): void {
    if (this.isAnyAntennaInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.ANTENNAINSTANCE, this.getSelectedAntennaInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.ANTENNA: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedAntennaIds())
          .then(
            success => {
              this.refreshAntennas();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.ANTENNAINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedAntennaInstanceIds())
          .then(
            success => {
              this.refreshAntennaInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  removeSelectedAntennasFromGroupClicked(): void {
    if (this.isAnyAntennasSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const antennaNames: string[] = [];
      this.getSelectedAntennas().forEach(antenna => {
        antennaNames.push(antenna.name);
        if (antenna.groups != null) {
          antenna.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, antennaNames);
    }
  }

  removeSelectedAntennaInstancesFromGroupClicked(): void {
    if (this.isAnyAntennaInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const antennaInstanceNames: string[] = [];
      this.getSelectedAntennaInstances().forEach(antennaInstance => {
        antennaInstanceNames.push(antennaInstance.name);
        if (antennaInstance.groups != null) {
          antennaInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, antennaInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.ANTENNA: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedAntennaIds())
          .then(
            success => {
              this.refreshAntennas();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.ANTENNAINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedAntennaInstanceIds())
          .then(
            success => {
              this.refreshAntennaInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.ANTENNA: {
        this
          .mGroupService
          .getAllAntennasInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.ANTENNAINSTANCE: {
        this
          .mGroupService
          .getAllAntennaInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }

  addAntennaClicked(): void {
    this.mEntity = new Antenna();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedAntennaClicked(): void {
    if (this.isSingleAntennaSelected()) {
      this.editAntenna(this.getSelectedAntennas()[0]);
    }
  }

  duplicateSelectedAntennaClicked(): void {
    if (this.isSingleAntennaSelected()) {
      this
        .mAntennaService
        .getAntennaById(this.getSelectedAntennas()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }
            if (response.functions != null) {
              response.functions.forEach(f => f.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedAntennasClicked(): void {
    if (this.isAnyAntennasSelected()) {
      this.mDeleteFlag = 1; //1 = Antennas
      this.mConfirmDeleteDialog.show("Antenna type", this.getSelectedAntennaNames());
    }
  }

  editAntenna(antenna: Antenna): void {
    this
      .mAntennaService
      .getAntennaById(antenna.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.entityParent = response.parent;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
            this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Antenna... Create it
      this
        .mAntennaService
        .createAntenna(this.mEntity)
        .then(
          response => {
            this.refreshAntennas();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Antenna... Update it
      this
        .mAntennaService
        .updateAntenna(this.mEntity)
        .then(
          response => {
            this.refreshAntennas();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  addAntennaInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new AntennaInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataRefreshReadersAndShowDialog();
  }

  editSelectedAntennaInstanceClicked(): void {
    if (this.isSingleAntennaInstanceSelected()) {
      this.editAntennaInstance(this.getSelectedAntennaInstances()[0]);
    }
  }

  duplicateSelectedAntennaInstanceClicked(): void {
    if (this.isSingleAntennaInstanceSelected()) {
      this
        .mAntennaService
        .getAntennaInstanceById(this.getSelectedAntennaInstances()[0].id)
        .then(
          response => {
            //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
            this.mInstance = response;

            //Set the Instance Name value so that it gets properly displayed in the Dialog Header
            this.mInstanceName = response.name;

            //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
            this.mInstance.id = null;
            this.mInstance.uuid = null;
            if (this.mInstance.data != null) {
              this.mInstance.data.forEach(d => d.id = null);
            }

            //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
            this.refreshTypesReloadDataRefreshReadersAndShowDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedAntennaInstancesClicked(): void {
    if (this.isAnyAntennaInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Antenna Instances
      this.mConfirmDeleteDialog.show("antenna", this.getSelectedAntennaInstanceNames());
    }
  }

  editAntennaInstance(readerInstance: AntennaInstance): void {
    this
      .mAntennaService
      .getAntennaInstanceById(readerInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataRefreshReadersAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataRefreshReadersAndShowDialog(): Promise<void> {
    //Create a List of the available Antennas that the Instance can be a type of
    this.mAvailableEntityTypes = await this.mAntennaService.getAllAntennas();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.antenna == null ? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.antenna.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.functions = this.mInstance.antenna.functions;
      this.mInstanceEntityType.properties = this.mInstance.antenna.properties;
    }

    //Refresh the available and selected Readers
    this.mSelectedReader = null;
    this
      .mReaderService
      .getAllReaderInstances()
      .then(
        response => {
          //Set the possible Reader values so that it can be used by the EntityInstanceDialogComponent
          this.mAvailableReaders = response;

          //Figure out if a Reader was previously selected for the Antenna, get the correct version and store it
          this.mSelectedReader = this.mInstance == null || this.mInstance.readerInstance == null ? null : this.mAvailableReaders.find(reader => reader.id == this.mInstance.readerInstance.id);


          // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
          this.cdr.detectChanges();

          //Refresh the Instance Data, loading the existing values in for the Instance being edited
          this.mEntityInstanceDialogComponent.refreshData();

          //Show the Entity Instance Dialog
          this.mEntityInstanceDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Antenna Instance to be created
    this.mInstance.antenna = this.mInstanceEntityType;
    this.mInstance.readerInstance = this.mSelectedReader;

    if (this.mInstance.id == null) {
      //ID is null, Antenna Instance does not exist!  Create it...
      this
        .mAntennaService
        .createAntennaInstance(this.mInstance)
        .then(
          response => {
            this.refreshAntennaInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Antenna Instance exists!  Update it...
      this
        .mAntennaService
        .updateAntennaInstance(this.mInstance)
        .then(
          response => {
            this.refreshAntennaInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: Antenna): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mAntennaService
        .getAntennaById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.functions = foundEntityType.functions;
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }

  selectedReaderChanged(reader: ReaderInstance): void {
    this.mSelectedReader = reader;
  }

  refreshInstanceData(): void {
    this
      .mAntennaService
      .getAntennaInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }


  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Antennas
      this
        .mAntennaService
        .deleteAntennas(this.getSelectedAntennaIds())
        .then(
          response => {
            // Needs work - If the user was on last page & he deleted all the items of the list, make sure the ui switches back to 2nd last page
            this.refreshAntennas();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Antenna Instances
      this
        .mAntennaService
        .deleteAntennaInstances(this.getSelectedAntennaInstanceIds())
        .then(
          response => {
            this.refreshAntennaInstance();
            }, error => this.handleError(error)
        );
    }
  }

  // function to handle the errors
  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }

    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }


  /*-----------------------FILTER FUNCTIONS FOR ANTENNA -------------------------*/

  private refreshFilterOptionsForAntenna(): void {
    while (this.filterOptionForAntenna.length > 0) {
      this.filterOptionForAntenna.pop();
    }

    this.filterOptionForAntenna.push(FilterOption_RemoveFilter);
    this.filterOptionForAntenna.push({ column: "name", label: "Name" });
  }


  onFilterOptionForAntennaClicked(filterOption: FilterOption): void {
    if (this.selectedFilterOptionForAntenna == filterOption) {
      return;
    }
    this.selectedFilterOptionForAntenna = filterOption;
    this.filterOptionValueForAntenna = "";
    this.currentPageForAntenna = 1;
    this.refreshAntennas();
  }


  onFilterValueForAntennaChanged(): void {
    this.currentPageForAntenna = 1;
    this.refreshAntennas();
  }


  filterStringChangedAntennas(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageForAntenna = pageNumber;
    this.pageChangedAntenna(pageNumber);
  }

  /*----------------------- FILTER FUNCTIONS FOR ANTENNA INSTANCES -------------------------*/

  private refreshFilterOptionsForAntennaInstance(): void {
    while (this.filterOptionForAntennaInstance.length > 0) {
      this.filterOptionForAntennaInstance.pop();
    }
    this.filterOptionForAntennaInstance.push(FilterOption_RemoveFilter);
    this.filterOptionForAntennaInstance.push({ column: "name", label: "Name" } as FilterOption);
  }


  onFilterOptionForAntennaInstanceClicked(filterOption: FilterOption): void {
    if (this.selectedFilterOptionForAntennaInstance == filterOption) {
      return;
    }
    this.selectedFilterOptionForAntennaInstance = filterOption;
    this.filterOptionValueForAntennaInstance = "";
    this.currentPageForAntennaInstance = 1;
    this.refreshAntennas();
  }


  onFilterValueForAntennaInstanceChanged(): void {
    this.currentPageForAntennaInstance = 1;
    this.refreshAntennaInstance();
  }


  filterStringChangedAntennaInstances(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageForAntennaInstance = pageNumber;
    this.pageChangedAntennaInstance(pageNumber);
  }

  /******************** PAGINATOR FUNCTIONS FOR ANTENNA  *****************/

  pageChangedAntenna(pageNumber?: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }
    if (this.currentPageForAntenna == pageNumber) {
      return;
    } 
    this.currentPageForAntenna = pageNumber;
    this.refreshAntennas();
  }


  private async refreshAntennas(): Promise<void> {
    this.spinnerService.show();
    let pagedData: PagedResults<Antenna>;
    try {
      pagedData = await this.mAntennaService.getAllAntennasForPaging((this.currentPageForAntenna - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterOptionValueForAntenna);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }
    // console.log("Current Page :" + this.currentPageForAntenna);
    // console.log("Total Types:" + this.totalNumberOfAntenna);
    // console.log("Selected Value:" + this.selectedPageSizeValueForTypes);
    this.updatePagedAntenna(pagedData);
    this.spinnerService.hide();
  }


  private updatePagedAntenna(pagedData: PagedResults<Antenna>): void {
    const uiObjects: AntennaUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(antenna => {
        const ui = new AntennaUI();
        ui.mAntenna = antenna;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }
    this.mAntennas = uiObjects;
    this.totalNumberOfAntenna = pagedData.size;
  }


  /******************** PAGINATOR FUNCTIONS FOR ANTENNA INSTANCES *****************/

  pageChangedAntennaInstance(pageNumber?: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }
    if (this.currentPageForAntennaInstance == pageNumber) {
      return;
    }
    this.currentPageForAntennaInstance = pageNumber;
    this.refreshAntennaInstance();
  }

  private async refreshAntennaInstance(): Promise<void> {
    this.spinnerService.show();
    let pagedData: PagedResults<AntennaInstance>;
    try {
      pagedData = await this.mAntennaService.getAllAntennaInstancesForPaging((this.currentPageForAntennaInstance - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.filterOptionValueForAntennaInstance);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }
    // console.log("Current Page :" + this.currentPageForAntennaInstance);
    // console.log("Total Types:" + this.totalNumberOfAntennaInstance);
    // console.log("Selected Value:" + this.selectedPageSizeValueForInstances);

    this.updatePagedAntennaInstance(pagedData);
    this.spinnerService.hide();
  }


  private updatePagedAntennaInstance(pagedData: PagedResults<AntennaInstance>): void {
    const uiObjects: AntennaInstanceUI[] = [];
    if (pagedData.data != null) {
      pagedData.data.forEach(antennaInstance => {
        const ui = new AntennaInstanceUI();
        ui.mAntennaInstance = antennaInstance;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }
    this.mAntennaInstances = uiObjects;

    this.totalNumberOfAntennaInstance = pagedData.size;
  }

  /*  
 *  Handling the Right click event only on Antenna Instance Name Column
 */
  fetchFunctionList(event: MouseEvent, antennaInstance: AntennaInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = []

    // Send the request to get the Antenna Instance details using the ID
    this
      .mAntennaService
      .getAntennaInstanceById(antennaInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.antenna && Array.isArray(response.antenna.functions) && (response.antenna.functions).length > 0) {
              this.functionList = response.antenna.functions;

            } else {
              // Input a dummy
              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
           this.handleError(error);
          }
        },
        error => this.handleError(error)
      );

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    if (item.id != -1) {
    // Display the Dialog Box for confirmation
    this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }


}

class AntennaUI {
  mSelected = false;
  mAntenna: Antenna;
}

class AntennaInstanceUI {
  mSelected = false;
  mAntennaInstance: AntennaInstance;
}

interface FilterOption {
  column: string;
  label: string;
}